import React, { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';
import { Settings, Save, Loader, CheckCircle, XCircle } from 'lucide-react';

const SettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    free_session_limit_enabled: true
  });
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .eq('setting_key', 'free_session_limit_enabled')
        .single();

      if (error) {
        console.error('Error loading settings:', error);
        // If setting doesn't exist, create it with default value
        if (error.code === 'PGRST116') {
          await createDefaultSetting();
        }
      } else if (data) {
        setSettings({
          free_session_limit_enabled: data.setting_value === true || data.setting_value === 'true'
        });
      }
    } catch (error) {
      console.error('Error in loadSettings:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultSetting = async () => {
    try {
      const { error } = await supabase
        .from('admin_settings')
        .insert({
          setting_key: 'free_session_limit_enabled',
          setting_value: true,
          description: 'Enable or disable the 4 free session limit for non-premium users'
        });

      if (error) throw error;
      
      setSettings({ free_session_limit_enabled: true });
    } catch (error) {
      console.error('Error creating default setting:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus(null);

      const { error } = await supabase
        .from('admin_settings')
        .update({
          setting_value: settings.free_session_limit_enabled,
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'free_session_limit_enabled');

      if (error) throw error;

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
        <p className="mt-4">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-management">
      <div className="content-header">
        <div>
          <h1>Settings</h1>
          <p className="text-gray-600 mt-2">Manage application settings and configurations</p>
        </div>
      </div>

      <div className="content-body">
        <div className="settings-section">
          <div className="settings-section-header">
            <Settings className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Session Limits</h2>
              <p className="text-sm text-gray-600 mt-1">Control free session access for non-premium users</p>
            </div>
          </div>

          <div className="settings-card">
            <div className="setting-item">
              <div className="setting-info">
                <h3 className="text-lg font-semibold text-gray-800">Free Session Limit</h3>
                <p className="text-sm text-gray-600 mt-1">
                  When enabled, non-premium users are limited to 4 free sessions. 
                  Premium users always have unlimited access.
                </p>
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-700">
                    <strong>Current Status:</strong> {settings.free_session_limit_enabled ? '✅ Enabled (4 session limit active)' : '❌ Disabled (Unlimited sessions for all users)'}
                  </p>
                </div>
              </div>

              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.free_session_limit_enabled}
                  onChange={() => handleToggle('free_session_limit_enabled')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="settings-actions">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>

            {saveStatus === 'success' && (
              <div className="save-status success">
                <CheckCircle className="w-5 h-5" />
                Settings saved successfully!
              </div>
            )}

            {saveStatus === 'error' && (
              <div className="save-status error">
                <XCircle className="w-5 h-5" />
                Failed to save settings. Please try again.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;